import { MIN_PASSWORD_LENGTH, STAFF_ROLE_LABELS_PL, type StaffUserPublic } from '@cfcg/shared';
import { useState, type FormEvent } from 'react';
import { Alert, Button, Card, Field, Input, Select } from '../components/ui.js';
import { messageOf, useStaffList, useStaffMutations } from '../lib/auth.js';

const PASSWORD_HINT = `Co najmniej ${MIN_PASSWORD_LENGTH} znaków. Liczy się długość, nie znaki specjalne.`;

function CreateStaffForm() {
  const { create } = useStaffMutations();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<'admin' | 'support'>('support');
  const [password, setPassword] = useState('');

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    create.mutate(
      { email, displayName, role, password },
      {
        onSuccess: () => {
          setEmail('');
          setDisplayName('');
          setPassword('');
          setRole('support');
        },
      },
    );
  }

  return (
    <Card title="Nowy pracownik">
      <form onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2">
        <Field label="Imię i nazwisko">
          <Input required value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </Field>
        <Field label="Adres e-mail">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Rola">
          <Select value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'support')}>
            <option value="support">{STAFF_ROLE_LABELS_PL.support}</option>
            <option value="admin">{STAFF_ROLE_LABELS_PL.admin}</option>
          </Select>
        </Field>
        <Field label="Hasło startowe" hint={PASSWORD_HINT}>
          <Input
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>

        <div className="sm:col-span-2 space-y-3">
          {create.isError && <Alert tone="error">{messageOf(create.error)}</Alert>}
          {create.isSuccess && (
            <Alert tone="success">
              Konto utworzone. Przekaż hasło startowe bezpiecznym kanałem — nie e-mailem.
            </Alert>
          )}
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Tworzenie…' : 'Utwórz konto'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function StaffRow({ user, isSelf }: { user: StaffUserPublic; isSelf: boolean }) {
  const { update, resetPassword, remove } = useStaffMutations();
  const [newPassword, setNewPassword] = useState('');
  const [showReset, setShowReset] = useState(false);

  return (
    <li className="border-t border-neutral-200 py-4 first:border-t-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-medium text-neutral-900">
            {user.displayName}
            {isSelf && <span className="ml-2 text-xs text-neutral-500">(to Ty)</span>}
            {!user.isActive && (
              <span className="ml-2 rounded bg-neutral-200 px-2 py-0.5 text-xs">nieaktywny</span>
            )}
          </p>
          <p className="text-sm text-neutral-500">{user.email}</p>
          <p className="text-xs text-neutral-400">
            {user.lastLoginAt
              ? `Ostatnie logowanie: ${new Date(user.lastLoginAt).toLocaleString('pl-PL')}`
              : 'Jeszcze się nie logował'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select
            className="w-48"
            value={user.role}
            onChange={(e) =>
              update.mutate({
                id: user.id,
                changes: { role: e.target.value as 'admin' | 'support' },
              })
            }
          >
            <option value="support">{STAFF_ROLE_LABELS_PL.support}</option>
            <option value="admin">{STAFF_ROLE_LABELS_PL.admin}</option>
          </Select>

          <Button
            variant="secondary"
            onClick={() => update.mutate({ id: user.id, changes: { isActive: !user.isActive } })}
          >
            {user.isActive ? 'Dezaktywuj' : 'Aktywuj'}
          </Button>

          <Button variant="secondary" onClick={() => setShowReset((v) => !v)}>
            Zmień hasło
          </Button>

          <Button
            variant="danger"
            onClick={() => {
              if (confirm(`Usunąć konto ${user.email}? Tej operacji nie da się cofnąć.`)) {
                remove.mutate(user.id);
              }
            }}
          >
            Usuń
          </Button>
        </div>
      </div>

      {showReset && (
        <form
          className="mt-3 flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            resetPassword.mutate(
              { id: user.id, password: newPassword },
              {
                onSuccess: () => {
                  setNewPassword('');
                  setShowReset(false);
                },
              },
            );
          }}
        >
          <div className="min-w-64 flex-1">
            <Field label="Nowe hasło" hint={PASSWORD_HINT}>
              <Input
                type="password"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </Field>
          </div>
          <Button type="submit" disabled={resetPassword.isPending}>
            Ustaw
          </Button>
        </form>
      )}

      {/* Zmiana roli, dezaktywacja i reset hasła natychmiast wylogowują
          tego pracownika ze wszystkich urządzeń — warto, żeby wiedział o tym
          administrator, zanim kliknie. */}
      {(update.isError || remove.isError || resetPassword.isError) && (
        <div className="mt-3">
          <Alert tone="error">
            {messageOf(update.error ?? remove.error ?? resetPassword.error)}
          </Alert>
        </div>
      )}
    </li>
  );
}

export function StaffPage({ currentUserId }: { currentUserId: string }) {
  const { data, isPending, error } = useStaffList(true);

  return (
    <div className="space-y-6">
      <Alert tone="info">
        Zmiana roli, dezaktywacja i reset hasła wylogowują pracownika ze wszystkich urządzeń
        natychmiast.
      </Alert>

      <CreateStaffForm />

      <Card title="Pracownicy">
        {isPending && <p className="text-sm text-neutral-500">Wczytywanie…</p>}
        {error && <Alert tone="error">{messageOf(error)}</Alert>}
        {data && (
          <ul>
            {data.users.map((user) => (
              <StaffRow key={user.id} user={user} isSelf={user.id === currentUserId} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
